export type ComponentTokenModule =
  | "apple_map"
  | "badge"
  | "body"
  | "bold"
  | "brand_icon"
  | "brand_illustration"
  | "button"
  | "checkbox_group"
  | "checkbox"
  | "collapse"
  | "drawer"
  | "dropdown_control"
  | "dropdown_group_heading"
  | "dropdown_group"
  | "dropdown_menu_list"
  | "dropdown_menu"
  | "dropdown_option"
  | "dropdown_placeholder"
  | "dropdown_single_value"
  | "dropdown_value_container"
  | "dropdown"
  | "file_upload"
  | "form_character_count"
  | "form_error"
  | "form_group_layout_wrapper"
  | "form_input_container"
  | "form_instructions"
  | "form_label"
  | "form"
  | "grid"
  | "heading"
  | "horizontal_line"
  | "icon"
  | "input_alignment_container"
  | "italic"
  | "link"
  | "logo"
  | "menu_button"
  | "menu_item"
  | "menu"
  | "modal"
  | "multi_select_dropdown"
  | "overlays"
  | "pager"
  | "page_title"
  | "paragraph"
  | "radio_group"
  | "radio"
  | "regular"
  | "search_textbox"
  | "semibold"
  | "shelf"
  | "sidebar_nav"
  | "spinner"
  | "stack"
  | "strikethrough"
  | "subheading"
  | "submit_button"
  | "subtitle"
  | "tabbed_container"
  | "table"
  | "tab_set"
  | "tag"
  | "telephone_textbox"
  | "text_area"
  | "textbox"
  | "title"
  | "tooltip"
  | "underlined";

const STYLE_TAG_PREFIX = "capitalrx-component-tokens-";
const FOUNDATION_STYLE_ID = "capitalrx-token-theme-foundation";
const DATA_THEME_ATTR = "data-theme";
const DEFAULT_THEME = "caprx";

/* ---------------------------------------------------------------------------
   Module-Federation-safe global cache (I-07)

   In MF layouts each remote gets its own module instance with its own
   module-scope state.  We use a shared global (`window.__CRX_LOADED_TOKENS__`)
   as a secondary fast-path cache while the DOM marker (`<meta>` element)
   remains the authoritative source of truth for whether a component's CSS
   has already been injected.
   --------------------------------------------------------------------------- */

const GLOBAL_CACHE_KEY = "__CRX_LOADED_TOKENS__";

interface GlobalTokenCache {
  loaded: Set<string>;
  inFlight: Map<string, Promise<void>>;
}

declare global {
  interface Window {
    [GLOBAL_CACHE_KEY]?: GlobalTokenCache;
  }
}

function getGlobalCache(): GlobalTokenCache {
  if (typeof window !== "undefined") {
    if (!window[GLOBAL_CACHE_KEY]) {
      window[GLOBAL_CACHE_KEY] = { loaded: new Set(), inFlight: new Map() };
    }
    return window[GLOBAL_CACHE_KEY]!;
  }
  // SSR fallback — module-scope cache
  return _ssrFallbackCache;
}

const _ssrFallbackCache: GlobalTokenCache = { loaded: new Set(), inFlight: new Map() };

async function ensureThemeFoundationLoaded(): Promise<void> {
  if (typeof document === "undefined") return;

  const cache = getGlobalCache();
  if (cache.loaded.has(FOUNDATION_STYLE_ID) || document.getElementById(FOUNDATION_STYLE_ID)) {
    cache.loaded.add(FOUNDATION_STYLE_ID);
  } else {
    await import(
      /* webpackChunkName: "token-theme-caprx" */
      // @ts-ignore TS2307: CSS is resolved by webpack at runtime in consuming shells.
      "../styles/global.css"
    );
    cache.loaded.add(FOUNDATION_STYLE_ID);

    const marker = document.createElement("meta");
    marker.id = FOUNDATION_STYLE_ID;
    marker.setAttribute("data-component", "theme-foundation");
    document.head.appendChild(marker);
  }

  if (!document.documentElement.getAttribute(DATA_THEME_ATTR)) {
    document.documentElement.setAttribute(DATA_THEME_ATTR, DEFAULT_THEME);
  }
}

// HMR support: reset state on hot reload
if (typeof module !== "undefined" && (module as unknown as { hot?: { dispose: (cb: () => void) => void } }).hot) {
  (module as unknown as { hot?: { dispose: (cb: () => void) => void } }).hot?.dispose(() => {
    const cache = getGlobalCache();
    cache.loaded.clear();
    cache.inFlight.clear();
  });
}

export async function loadComponentTokens(component: ComponentTokenModule): Promise<void> {
  if (typeof document === "undefined") return;

  const styleId = `${STYLE_TAG_PREFIX}${component}`;
  const cache = getGlobalCache();

  // DOM marker is the authoritative source of truth (works across MF boundaries)
  if (cache.loaded.has(component) || document.getElementById(styleId)) {
    // Sync the global cache if DOM marker exists but cache doesn't know
    cache.loaded.add(component);
    return;
  }

  // Deduplicate concurrent calls for the same component
  const existing = cache.inFlight.get(component);
  if (existing) {
    return existing;
  }

  const MAX_RETRIES = 2;

  const loadPromise = (async () => {
    try {
      await ensureThemeFoundationLoaded();

      let lastError: unknown;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
          await import(
            /* webpackChunkName: "token-[request]" */
            `../dist/components/${component}.css`
          );
          lastError = undefined;
          break;
        } catch (error) {
          lastError = error;
          if (attempt < MAX_RETRIES) {
            // Brief delay before retry (100ms, 200ms)
            await new Promise((r) => setTimeout(r, (attempt + 1) * 100));
          }
        }
      }

      if (lastError) {
        throw lastError;
      }

      cache.loaded.add(component);

      // Add a marker element so the DOM check in getElementById works across reloads
      if (!document.getElementById(styleId)) {
        const marker = document.createElement("meta");
        marker.id = styleId;
        marker.setAttribute("data-component", component);
        document.head.appendChild(marker);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`[loadComponentTokens] Failed to load tokens for component "${component}":`, error);
      }
    } finally {
      cache.inFlight.delete(component);
    }
  })();

  cache.inFlight.set(component, loadPromise);
  return loadPromise;
}
export async function loadComponentTokensBatch(components: ComponentTokenModule[]): Promise<void> {
  await Promise.all(components.map((component) => loadComponentTokens(component)));
}
export function isComponentTokensLoaded(component: ComponentTokenModule): boolean {
  return getGlobalCache().loaded.has(component);
}
/**
 * Clears the in-memory loaded-components cache **and** removes all injected
 * DOM marker elements (`<meta id="capitalrx-component-tokens-*">`).
 *
 * After calling this, subsequent `loadComponentTokens()` calls will re-import
 * the component CSS and re-create DOM markers.
 */
export function clearLoadedComponentsCache(): void {
  const cache = getGlobalCache();
  cache.loaded.clear();

  /* I-06: also remove DOM markers so the getElementById guard doesn't
     prevent re-loading after a cache clear. */
  if (typeof document !== "undefined") {
    document.querySelectorAll(`meta[id^="${STYLE_TAG_PREFIX}"]`).forEach((el) => el.remove());
    document.getElementById(FOUNDATION_STYLE_ID)?.remove();
  }
}
